/**
 * Signup Page
 *
 * Email/password + Google. Same design language as Login. Includes inline
 * validation: required fields, email format, ≥8-char password, match check.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSeo } from '@/hooks/useSeo';

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none">
      <path d="M21.6 12.227c0-.704-.063-1.382-.181-2.034H12v3.85h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.232c1.891-1.742 2.981-4.305 2.981-7.344Z" fill="#4285F4" />
      <path d="M12 22c2.7 0 4.964-.895 6.619-2.43l-3.232-2.508c-.895.6-2.04.954-3.387.954-2.604 0-4.81-1.76-5.596-4.122H3.064v2.59A9.997 9.997 0 0 0 12 22Z" fill="#34A853" />
      <path d="M6.404 13.894A6.003 6.003 0 0 1 6.09 12c0-.659.114-1.298.314-1.894V7.516H3.064A9.997 9.997 0 0 0 2 12c0 1.614.385 3.14 1.064 4.484l3.34-2.59Z" fill="#FBBC05" />
      <path d="M12 5.984c1.471 0 2.79.506 3.829 1.499l2.872-2.872C16.96 2.99 14.696 2 12 2A9.997 9.997 0 0 0 3.064 7.516l3.34 2.59C7.19 7.745 9.396 5.984 12 5.984Z" fill="#EA4335" />
    </svg>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Signup() {
  useSeo({
    title: 'Start free · aicostr',
    description:
      'Create your aicostr account — real-time AI cost tracking across every LLM provider. No credit card required.',
    canonicalPath: '/signup',
  });
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();
  const { error: showError, success: showSuccess } = useToast();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState<'email' | 'google' | null>(null);

  const validate = (): string | null => {
    if (!name.trim()) return 'Please enter your name.';
    if (!email) return 'Please enter your email.';
    if (!EMAIL_RE.test(email)) return 'Please enter a valid email address.';
    if (!password) return 'Please enter a password.';
    if (password.length < 8) return 'Password must be at least 8 characters.';
    if (password !== confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      showError('Check your input', error);
      return;
    }

    setLoading('email');
    try {
      await signup(email, password, name);
      showSuccess('Welcome', 'Your Aicostr account is ready.');
      navigate('/onboarding');
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      let message = 'Could not create your account. Please try again.';
      if (code === 'auth/email-already-in-use')
        message = 'An account with this email already exists.';
      else if (code === 'auth/invalid-email') message = 'Invalid email format.';
      else if (code === 'auth/weak-password')
        message = 'Password is too weak — please use at least 8 characters.';
      showError('Sign up failed', message);
    } finally {
      setLoading(null);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading('google');
    try {
      await loginWithGoogle();
      showSuccess('Welcome', 'Your Aicostr account is ready.');
      navigate('/onboarding');
    } catch (err: unknown) {
      const message =
        (err as Error)?.message || 'Could not sign up with Google.';
      showError('Sign up failed', message);
    } finally {
      setLoading(null);
    }
  };

  const busy = loading !== null;

  return (
    <AuthShell
      size="wide"
      title="Create your account"
      subtitle="Start tracking your AI costs in under a minute."
      footer={
        <>
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </>
      }
    >
      <div className="space-y-4">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={handleGoogleSignup}
          disabled={busy}
        >
          {loading === 'google' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <GoogleMark className="size-4" />
          )}
          Continue with Google
        </Button>

        <div className="relative my-2 flex items-center">
          <div className="grow border-t border-hairline" />
          <span className="px-3 text-[11px] uppercase tracking-wider text-muted-foreground">
            or
          </span>
          <div className="grow border-t border-hairline" />
        </div>

        <form onSubmit={handleEmailSignup} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="signup-name">Full name</Label>
            <Input
              id="signup-name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Cooper"
              disabled={busy}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="signup-email">Email</Label>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={busy}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="signup-password">Password</Label>
            <Input
              id="signup-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              disabled={busy}
            />
            <p className="text-xs text-muted-foreground">
              Use 8+ characters with a mix of letters, numbers and symbols.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="signup-confirm">Confirm password</Label>
            <Input
              id="signup-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              disabled={busy}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            By creating an account, you agree to our{' '}
            <Link
              to="/terms"
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              Privacy Policy
            </Link>
            .
          </p>

          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {loading === 'email' ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <UserPlus className="size-4" />
            )}
            {loading === 'email' ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
